import React, { useState, useRef, useMemo, useCallback } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Avatar } from "primereact/avatar";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";
import { Dialog } from "primereact/dialog";
import { toastAlert } from "../../core/utils";
import LeftSidebar from "../../components/LeftSidebar";
import ButtonsActions from "../../components/ButtonsActions";
import { AssignmentService } from "../../service/AssignmentService";

const DesignerDropdownTemplate = ({ option, selected }) => (
    <div className="flex justify-content-start align-items-center">
        <Avatar image={option.img} shape="circle" />
        <div className="ml-2">{option.name}</div>
    </div>
);

const DesignerInfoCard = ({ designer, totalAssignments, onRemove }) => (
    <div className="shadow-2 p-4 surface-card" style={{ borderRadius: "1rem" }}>
        <div className="flex justify-content-between align-items-baseline">
            <h5 className="mb-4 text-900 font-medium">Designer Information</h5>
            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" tooltip="Remove" tooltipOptions={{ position: "top" }} onClick={onRemove} />
        </div>

        <div className="flex">
            <div className="flex flex-column w-9 pr-4">
                <span className="mb-3 text-900 font-medium text-green-700">
                    {designer._NAME} {designer._LASTNAME}
                </span>
                <span className="mb-3">
                    <div className="mb-1">
                        <span className="text-900 font-medium">Email: </span>
                        {designer._EMAIL}
                    </div>
                    <div className="mb-1">
                        <span className="text-900 font-medium">Phone: </span>
                        {designer._PHONE}
                    </div>
                    <div className="mb-1">
                        <span className="text-900 font-medium">Total assignments: </span>
                        <Badge value={totalAssignments} />
                    </div>
                </span>
            </div>
            <div className="w-3 flex align-items-start justify-content-end">
                <Avatar image={designer._AVATAR} size="xlarge" />
            </div>
        </div>
    </div>
);

function EditAdd({ idRequest, onVisible, onHide, designers, dataList, updateDesignerAssignment, currentDesigner }) {
    const dataService = useRef(new AssignmentService());
    const toast = useRef(null);

    const [loadingButton, setLoadingButton] = useState(false);
    const [selectedDesignerId, setSelectedDesignerId] = useState("");
    const [showInfo, setShowInfo] = useState(false);
    const [currentDesignerData, setCurrentDesignerData] = useState(null);
    const [requestTotal, setRequestTotal] = useState(0);

    const designerOptions = useMemo(
        () =>
            designers.map((d) => ({
                name: `${d._NAME} ${d._LASTNAME}`,
                value: d._ID,
                img: d._AVATAR,
            })),
        [designers]
    );

    const calculateDesignerRequestTotal = useCallback(
        (designer) => {
            if (!dataList) return 0;

            const fullname = `${designer._NAME} ${designer._LASTNAME}`;
            return dataList.filter((list) => list._ASSIGNED === fullname).length;
        },
        [dataList]
    );

    const handleDesignerChange = useCallback(
        (e) => {
            const designerId = e.value;
            setSelectedDesignerId(designerId);

            const selectedDesigner = designers.find((it) => it._ID === designerId);
            if (!selectedDesigner) return;

            setShowInfo(true);
            setCurrentDesignerData(selectedDesigner);
            setRequestTotal(calculateDesignerRequestTotal(selectedDesigner));
        },
        [designers, calculateDesignerRequestTotal]
    );

    const removeDesigner = useCallback(() => {
        setSelectedDesignerId("");
        setShowInfo(false);
        setCurrentDesignerData(null);
    }, []);

    const hideAndRemove = useCallback(() => {
        removeDesigner();
        onHide();
    }, [removeDesigner, onHide]);

    const sendData = useCallback(async () => {
        setLoadingButton(true);

        try {
            const data = {
                _IDREQUEST: idRequest,
                _DESIGNER: selectedDesignerId,
            };

            const response = await dataService.current.updateData(data);
            setLoadingButton(false);

            if (response._SUCCESS) {
                toastAlert("success", response._MESSAGE, toast);
                setTimeout(() => {
                    updateDesignerAssignment(response._ITEMS);
                    hideAndRemove();
                }, 500);
            } else {
                toastAlert("warn", response._MESSAGE, toast);
            }
        } catch (error) {
            setLoadingButton(false);
            toastAlert(error.variant, error.data, toast);
        }
    }, [idRequest, selectedDesignerId, updateDesignerAssignment, hideAndRemove]);

    return (
        <div>
            <LeftSidebar onVisible={onVisible} onHide={onHide} onWidth={500}>
                <Toast ref={toast} style={{ fontSize: "14px" }} />

                <div className="p-fluid formgrid grid">
                    <div className="field col-12">
                        <h2 className="text-color">Re-Assign Request</h2>
                        <h5>
                            Current Designer: <b className="text-green-600">{currentDesigner}</b>
                        </h5>
                    </div>

                    <div className="field col-12">
                        <label htmlFor="name" className="font-semibold">
                            Designers
                        </label>
                        <Dropdown
                            value={selectedDesignerId}
                            options={designerOptions}
                            optionLabel="name"
                            filter
                            filterBy="name"
                            placeholder="Select a Designer"
                            onChange={handleDesignerChange}
                            itemTemplate={(option) => <DesignerDropdownTemplate option={option} />}
                            valueTemplate={(option, props) => (option ? <DesignerDropdownTemplate option={option} selected /> : <span>{props.placeholder}</span>)}
                        />
                    </div>

                    {showInfo && currentDesignerData && (
                        <div className="field col-12">
                            <div className="p-2">
                                <DesignerInfoCard designer={currentDesignerData} totalAssignments={requestTotal} onRemove={removeDesigner} />
                            </div>
                        </div>
                    )}

                    <Dialog visible={loadingButton} style={{ width: "450px" }} showHeader={false} modal onHide={() => {}}>
                        <div className="text-center">
                            <div className="grid">
                                <div className="col-12">
                                    {currentDesignerData && (
                                        <p className="text-2xl p-4">
                                            Reassigning request to{" "}
                                            <b>
                                                {currentDesignerData._NAME} {currentDesignerData._LASTNAME}
                                            </b>
                                            , please wait...
                                        </p>
                                    )}
                                </div>
                                <div className="col-12">
                                    <ProgressBar mode="indeterminate" className="w-full text-color h-1rem" />
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </div>

                <ButtonsActions cancel={hideAndRemove} submit={sendData} loading={loadingButton} />
            </LeftSidebar>
        </div>
    );
}

export default React.memo(EditAdd);
